import { IHousingObjectResponse } from '@/types';
import { RealEstateListingSchema } from '@/types/structuredData';
import { normalizeUrl } from '../normalizeUrl';
import { normalizeText } from '../normalizeText';

const CATEGORY_SCHEMA_TYPE: Record<number, string> = {
  27: 'Apartment',
  8: 'SingleFamilyResidence',
  17: 'House',
  111: 'Room',
};

function getSchemaTypes(
  categoryId: number | null,
  hasPrice: boolean,
  hasImage: boolean,
): string | string[] {
  const specific = categoryId != null ? CATEGORY_SCHEMA_TYPE[categoryId] : undefined;
  const base = hasPrice && hasImage ? ['Product', 'RealEstateListing'] : ['RealEstateListing'];
  return specific ? [...base, specific] : base;
}

export const generateListingDetailSchema = (
  data: IHousingObjectResponse,
  pageUrl: string,
): RealEstateListingSchema => {
  const isSell = data.sell;
  const price = isSell
    ? data.prices?.sellPrice
    : data.prices?.monthlyCosts || data.prices?.grossRent;
  const numericPriceRaw = isSell
    ? data.prices?.sellPriceNumeric
    : data.prices?.monthlyCostsNumeric || data.prices?.grossRentNumeric;
  const currency = data.currencyCode || 'EUR';
  const hasPrice = !!(price && price !== 'Preis auf Anfrage' && price !== 'Auf Anfrage');
  // Fall back to parsing the formatted price string when the numeric field is missing.
  const numericPrice: number | null =
    numericPriceRaw != null
      ? numericPriceRaw
      : hasPrice && price
        ? (() => {
            const match = price.match(/[\d.,]+/);
            if (!match) return null;
            const parsed = parseFloat(match[0].replace(/\./g, '').replace(',', '.'));
            return isNaN(parsed) ? null : parsed;
          })()
        : null;

  const images: string[] = [];
  if (data.media?.image && Array.isArray(data.media.image)) {
    data.media.image.forEach((img: { link: string }) => {
      if (img.link) images.push(img.link);
    });
  }
  if (images.length === 0 && data.imageList && Array.isArray(data.imageList)) {
    data.imageList.forEach((img) => {
      if (img.imageFullUrl) images.push(img.imageFullUrl);
    });
  }
  if (images.length === 0 && data.imageFullUrl) {
    images.push(data.imageFullUrl);
  }

  const addressParts: string[] = [];
  if (data.address) addressParts.push(data.address);
  if (data.postcode) addressParts.push(data.postcode);
  if (data.place) addressParts.push(data.place);

  const schema: RealEstateListingSchema = {
    '@context': 'https://schema.org',
    '@type': getSchemaTypes(data.categoryId, hasPrice, images.length > 0),
    name: normalizeText(data.title) || '',
    description: normalizeText(data.description) || undefined,
    image: images.length > 0 ? (images.length === 1 ? images[0] : images) : undefined,
    url: pageUrl,
    address:
      addressParts.length > 0
        ? {
            '@type': 'PostalAddress',
            streetAddress: data.address || undefined,
            addressLocality: data.place || undefined,
            postalCode: data.postcode || undefined,
            addressCountry: data.countryTitle || 'AT',
          }
        : undefined,
    geo:
      data.latitude && data.longitude
        ? {
            '@type': 'GeoCoordinates',
            latitude: data.latitude,
            longitude: data.longitude,
          }
        : undefined,
    category: data.categoryTitle || undefined,
  };

  // offers
  const availability =
    data.otherDetails?.obtainableFromText || data.otherDetails?.obtainableFromDate
      ? 'https://schema.org/PreOrder'
      : 'https://schema.org/InStock';

  if (hasPrice && numericPrice != null) {
    schema.offers = {
      '@type': 'Offer',
      priceCurrency: currency,
      availability,
      businessFunction: isSell ? 'https://schema.org/Sell' : 'https://schema.org/LeaseOut',
      price: numericPrice.toString(),
      priceSpecification: {
        '@type': 'PriceSpecification',
        price: numericPrice.toString(),
        priceCurrency: currency,
      },
    };
  }

  // floorSize
  if (data.squares?.areaDetailed) {
    const areaMatch = data.squares.areaDetailed.match(/(\d+(?:[.,]\d+)?)/);
    if (areaMatch) {
      schema.floorSize = {
        '@type': 'QuantitativeValue',
        value: areaMatch[1].replace(',', '.'),
        unitCode: 'MTK',
      };
    }
  }

  // numberOfRooms
  if (data.otherDetails?.roomNumber) {
    const roomMatch = data.otherDetails.roomNumber.match(/(\d+(?:[.,]\d+)?)/);
    if (roomMatch) {
      schema.numberOfRooms = {
        '@type': 'QuantitativeValue',
        value: roomMatch[1].replace(',', '.'),
      };
    }
  }

  // agent
  if (data.brokerData) {
    const brokerAddress = data.brokerData.address;
    const brokerAddressParts: string[] = [];
    if (brokerAddress?.street) brokerAddressParts.push(brokerAddress.street);
    if (brokerAddress?.streetNumber) brokerAddressParts.push(brokerAddress.streetNumber);

    schema.agent = {
      '@type': 'RealEstateAgent',
      name: data.brokerData.name || '',
      image: data.brokerData.imageFullUrl || undefined,
      url: data.brokerData.contactSite ? normalizeUrl(data.brokerData.contactSite) : undefined,
      address: brokerAddress
        ? {
            '@type': 'PostalAddress',
            streetAddress: brokerAddressParts.join(' ') || undefined,
            addressLocality: brokerAddress.place || undefined,
            postalCode: brokerAddress.postcode || undefined,
            addressCountry: 'AT',
          }
        : undefined,
    };

    const officeName = data.brokerData.officeName || data.brokerData.brokerOfficeName;
    if (officeName && officeName !== data.brokerData.name) {
      schema.agent.worksFor = {
        '@type': 'Organization',
        name: officeName,
      };
    }
  }

  // lotSize
  if (data.squares?.groundArea) {
    const groundMatch = data.squares.groundArea.match(/(\d+(?:[.,]\d+)?)/);
    if (groundMatch) {
      schema.lotSize = {
        '@type': 'QuantitativeValue',
        value: groundMatch[1].replace(',', '.'),
        unitCode: 'MTK',
      };
    }
  }

  // numberOfBathroomsTotal
  if (data.otherDetails?.bathsNumber) {
    const bathsMatch = data.otherDetails.bathsNumber.match(/(\d+)/);
    if (bathsMatch) {
      schema.numberOfBathroomsTotal = parseInt(bathsMatch[1], 10);
    }
  }

  // yearBuilt
  if (data.otherDetails?.buildYear) {
    const yearMatch = data.otherDetails.buildYear.match(/(\d{4})/);
    if (yearMatch) {
      schema.yearBuilt = yearMatch[1];
    }
  }

  // additionalProperty — energy data
  const additionalProperty: RealEstateListingSchema['additionalProperty'] = [];
  if (data.otherDetails?.powerConsumptionClass) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Energieklasse',
      value: data.otherDetails.powerConsumptionClass,
    });
  }
  if (data.otherDetails?.energyClassFactor) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Heizwärmebedarf-Klasse',
      value: data.otherDetails.energyClassFactor,
    });
  }
  if (data.otherDetails?.energyIndex) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Heizwärmebedarf',
      value: data.otherDetails.energyIndex,
    });
  }
  if (additionalProperty.length > 0) {
    schema.additionalProperty = additionalProperty;
  }

  return schema;
};
